import { celebrate } from '@lib/confetti';
import { createAsyncSelectors } from '@lib/selectors';
import { getDirectParent } from '@lib/popups';
import { InitiativeStatusDisplay } from './InitiativeStatusDisplay';

import { coreActions, InitiativeStatus } from '@sonar-software/sonar-core';
import { Else, If, Then } from 'react-if';
import React, { useCallback, useMemo } from 'react';
import { Select, SelectProps } from 'antd';
import { useAction, useCurrentUser, useOnMount, useSonarSelector } from '@hooks';

let mounted = false;
const selectors = createAsyncSelectors((state) => state.core.initiatives.statuses.get);

export interface InitiativeStatusSelectorProps extends Omit<SelectProps<string>, 'onChange' | 'value'> {
	id?: string;
	loading?: boolean;
	noConfetti?: boolean;
	onChange?: (statusId: InitiativeStatus) => void;
	showArrow?: boolean;
	value?: InitiativeStatus;
}

export const InitiativeStatusSelector: React.FC<InitiativeStatusSelectorProps> = ({
	defaultValue,
	disabled,
	id,
	loading,
	noConfetti,
	onChange,
	showArrow,
	value,
	...rest
}) => {
	const { isReadOnly } = useCurrentUser();
	const statuses = useSonarSelector(selectors.data);

	const loadStatuses = useAction(coreActions.requestGetCoreInitiativesStatuses);

	useOnMount(() => {
		if (!statuses && !mounted) {
			mounted = true;
			loadStatuses();
		}
	});

	const statusHandlers = useMemo(() => {
		if (!statuses) {
			return [];
		}

		return statuses.map((status) => {
			const { id: statusId } = status;

			return {
				label: <InitiativeStatusDisplay statusId={ statusId } />,
				value: String(statusId)
			};
		});
	}, [ statuses ]);

	const handleChange = useCallback(
		(statusId: string) => {
			const newStatusId = Number(statusId) as InitiativeStatus;

			if (onChange) {
				onChange(newStatusId);
			}

			if (newStatusId === InitiativeStatus.Finished && !noConfetti) {
				celebrate();
			}
		},
		[ noConfetti, onChange ]
	);

	if (!statuses || loading) {
		return (
			<Select
				disabled
				options={ statusHandlers }
				value={ value ? String(value) : undefined } />
		);
	}

	const defaultValueNumber = Number(defaultValue) || Number(value);
	const displayValue = value ?? (isNaN(defaultValueNumber) ? defaultValue : defaultValueNumber);
	const readOnlyLabel = isNaN(defaultValueNumber) ?
		displayValue	 :
		<InitiativeStatusDisplay statusId={ defaultValueNumber } />;

	return (
		<If condition={ !isReadOnly }>
			<Then>
				<Select
					defaultValue={ defaultValue || String(value) }
					disabled={ disabled }
					getPopupContainer={ getDirectParent }
					id={ id }
					onChange={ handleChange }
					options={ statusHandlers }
					suffixIcon={ showArrow ? undefined : null }
					value={ value ? String(value) : undefined }
					{ ...rest } />
			</Then>
			<Else>{ readOnlyLabel }</Else>
		</If>
	);
};
